import { ref } from 'vue';
import MQTTService from '@/js/mqtt';
import useConfig from '@/composables/useConfig';
import useVisibility from '@/composables/useVisibility';

const isOnline = ref(window.navigator.onLine);
let mqtt = null;
let offlineTimeout = null;
export default function useMqtt() {
  const { visibilityHidden } = useVisibility();

  function initMQTT() {
    const { initialConfig } = useConfig();
    const { robotId, organisationId } = initialConfig.value;
    if (robotId && organisationId) {
      mqtt = new MQTTService(robotId, organisationId);
    }
  }

  function isInitialized() {
    return mqtt !== null;
  }

  function isConnected() {
    return mqtt && mqtt.isConnected();
  }

  async function connect() {
    if (mqtt && !mqtt.isConnected()) {
      await mqtt.connect();
      isOnline.value = true;
      mqtt.client.on('connect', () => {
        isOnline.value = true;
        clearTimeout(offlineTimeout);
      });
      mqtt.client.on('offline', () => {
        if (!visibilityHidden.value) {
          offlineTimeout = setTimeout(() => {
            isOnline.value = false;
          }, 1000 * 10);
        }
      });
    }
  }

  /**
   * Publishes message to the mqtt
   * @param scope - the scope of the message. [robot, public]
   * @param topic - message topic
   * @param message - message to publish
   * @returns {Promise<void>}
   */
  async function mqttPublish({ scope, topic }, message) {
    if (!isConnected()) {
      return;
    }
    await mqtt.publish({ scope, suffix: topic }, message);
  }

  /**
   * Subscribes to the mqtt message
   * @param scope - the scope of the message. [robot, public]
   * @param topic - message topic
   * @param handler - message handler function
   * @returns {Promise<void>}
   */
  async function mqttSubscribe({ scope, topic }, handler) {
    if (!isConnected()) {
      return;
    }
    await mqtt.subscribe({ scope, suffix: topic }, handler);
  }

  /**
   * Unsubscribes from the topic
   * @param scope - the scope of the message. [robot, public]
   * @param topic - message topic
   * @param handler - message handler function
   * @returns {Promise<void>}
   */
  async function mqttUnsubscribe({ scope, topic }, handler) {
    if (!isConnected()) {
      return;
    }
    await mqtt.unsubscribe({ scope, suffix: topic }, handler);
  }

  async function deleteMQTTInstance() {
    if (mqtt) {
      await mqtt.closeClient();
    }

    mqtt = null;
  }

  return {
    isOnline,
    initMQTT,
    isConnected,
    isInitialized,
    connect,
    mqttPublish,
    mqttSubscribe,
    mqttUnsubscribe,
    deleteMQTTInstance,
  };
}
